import { atom } from "recoil"
import { v4 as uuidv4 } from "uuid"

import { defaultPluginTemplates } from "@fuse/utils/defaultPluginTemplates"

import {
  LiveData,
  NEW_PLUGIN_ID_PREFIX,
  PanelState,
  Plugin,
  PluginFileState,
  PluginStudioState,
  PluginStudioUIState,
  TestCase,
  TestCaseUser,
  toCodeEditorPluginId,
  toLivePreviewPluginId,
  UserRole,
} from "../types"

// Export UIState as an alias for PluginStudioUIState for backward compatibility
export type UIState = PluginStudioUIState

// No longer using recoil-persist
// import { recoilPersist } from "recoil-persist"
// const { persistAtom } = recoilPersist({
//   key: "pluginStudio_consolidated",
//   storage: localStorage,
// })

// Generate default IDs that will be used if no data is available
const DEFAULT_EVENT_ID = `default-event-${uuidv4().slice(0, 8)}`
const DEFAULT_HOST_USER_ID = `host-user-${uuidv4().slice(0, 8)}`
const DEFAULT_GUEST_USER_ID = `guest-user-${uuidv4().slice(0, 8)}`
const DEFAULT_TEST_CASE_ID = uuidv4()

// Create a default starter plugin
const createDefaultPlugin = (): Plugin => {
  const timestamp = Date.now()
  const pluginId = `${NEW_PLUGIN_ID_PREFIX}${timestamp}`

  // Use templates from the defaultPluginTemplates file
  const guestCode = defaultPluginTemplates[UserRole.GUEST]
  const hostEventViewCode =
    defaultPluginTemplates[UserRole.HOST_EVENT_VIEW] || ""
  const hostConfigCode = defaultPluginTemplates[UserRole.HOST_CONFIG_VIEW] || ""

  return {
    id: pluginId,
    name: "Starter Plugin",
    description:
      "A simple starter plugin with notes and activity feed features",
    manifest: {
      name: "Starter Plugin",
      description:
        "A simple starter plugin with notes and activity feed features",
      version: "1.0.0",
      author: "You",
      category: "utilities",
      tags: ["starter", "notes", "activity"],
      iconName: "lightning",
    },
    files: [
      {
        id: uuidv4(),
        name: "GuestView.tsx",
        path: `/frontend/GuestView.tsx`,
        content: guestCode,
        type: UserRole.GUEST,
        lastModified: timestamp,
      },
      {
        id: uuidv4(),
        name: "HostEventView.tsx",
        path: `/frontend/HostEventView.tsx`,
        content: hostEventViewCode,
        type: UserRole.HOST_EVENT_VIEW,
        lastModified: timestamp,
      },
      {
        id: uuidv4(),
        name: "HostConfigView.tsx",
        path: `/frontend/HostConfigView.tsx`,
        content: hostConfigCode,
        type: UserRole.HOST_CONFIG_VIEW,
        lastModified: timestamp,
      },
    ],
    // Add code property to match expected interface
    code: {
      frontendGuestCodeWeb: guestCode,
      frontendHostViewCodeWeb: hostEventViewCode,
      frontendHostConfigCodeWeb: hostConfigCode,
    },
    createdAt: timestamp,
    isNew: true,
  }
}

// Create a default test case
const createDefaultTestCase = (): TestCase => {
  return {
    id: DEFAULT_TEST_CASE_ID,
    name: "Default Test Case",
    eventData: {
      id: DEFAULT_EVENT_ID,
      title: "Example Event",
      description: "This is an example event for testing plugins",
      date: new Date().toISOString(),
      location: {
        name: "Test Location",
        address: "123 Test St, Test City",
        coordinates: {
          latitude: 37.7749,
          longitude: -122.4194,
        },
      },
      flyerImage: "",
    },
    pluginConfig: {
      enabled: true,
      settings: {
        showGuestList: true,
        allowComments: true,
      },
    },
    eventPluginData: {},
    settings: {
      triggerDelay: 0,
      simulateNetworkDelay: false,
    },
  }
}

// Create a default starter plugin
const defaultPlugin = createDefaultPlugin()

// Create default live data with two users
const createDefaultLiveData = (): LiveData => {
  return {
    sourceTestCaseId: DEFAULT_TEST_CASE_ID,
    eventData: {
      id: DEFAULT_EVENT_ID,
      name: "Default Event",
      description: "Default event for plugin testing",
      date: new Date().toISOString(),
      location: {
        name: "Test Location",
        address: "123 Test St, Test City",
      },
      flyerImage: "",
    },
    pluginConfig: {
      enabled: true,
      settings: {
        showGuestList: true,
        allowComments: true,
      },
    },
    eventPluginData: {},
    users: [
      {
        id: DEFAULT_GUEST_USER_ID,
        name: "Guest User",
        role: UserRole.GUEST,
        viewAs: UserRole.GUEST,
        userData: {
          id: DEFAULT_GUEST_USER_ID,
          displayName: "Guest User",
          role: UserRole.GUEST,
        },
        userPluginData: {},
      },
      {
        id: DEFAULT_HOST_USER_ID,
        name: "Host User",
        role: UserRole.HOST_EVENT_VIEW,
        viewAs: UserRole.HOST_EVENT_VIEW,
        userData: {
          id: DEFAULT_HOST_USER_ID,
          displayName: "Host User",
          role: UserRole.HOST_EVENT_VIEW,
        },
        userPluginData: {},
      },
    ],
    activeUserId: DEFAULT_GUEST_USER_ID,
    livePreviewPluginId: toLivePreviewPluginId(defaultPlugin.id),
    isDirty: false,
  }
}

// Using interfaces imported from types.ts instead of redefining them
export const pluginStudioAtom = atom<PluginStudioState>({
  key: "pluginStudio.consolidated",
  default: {
    plugins: [defaultPlugin],
    testCases: [createDefaultTestCase()],
    activeTestCaseId: DEFAULT_TEST_CASE_ID,
    liveData: createDefaultLiveData(),
    codeEditorPluginId: toCodeEditorPluginId(defaultPlugin.id),
    pluginStates: {
      [defaultPlugin.id]: {
        activeFileId:
          defaultPlugin.files.find((f) => f.type === UserRole.GUEST)?.id ||
          null,
        openFileIds: [
          defaultPlugin.files.find((f) => f.type === UserRole.GUEST)?.id || "",
        ],
        activeViewType: UserRole.GUEST,
      },
    },
    isPreviewStale: false,
    uiState: {
      panels: {
        fileExplorer: {
          visible: true,
          width: "250px",
        },
        testPanel: {
          visible: true,
          height: "44.257%",
        },
        liveDataPanel: {
          visible: true,
          height: "44.257%",
        },
        previewPanel: {
          visible: true,
          width: "35%",
        },
      },
    },
  },
})
